import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import MedidaConMm from "../../components/design-system/MedidaConMm";

// ─────────────────────────────────────────────────────────────────────────
// ExpedienteCompra — bloque "Expediente" de la ficha de una OC (21-oct-2026).
//
// Las cuatro piezas de una compra, guardadas como archivos (no regeneradas):
//   1. Solicitud tal como se envió   2. Cotización del proveedor
//   3. OC tal como se confirmó       4. Factura del proveedor
// Cada una con fecha y quién. Lo que falta se ve en ámbar con el botón para resolverlo.
// Pensado para el teléfono: lista vertical, botones altos y de ancho completo, sin tablas.
// ─────────────────────────────────────────────────────────────────────────

const fmtFecha = d => d ? new Date(d).toLocaleString("es-CL", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "";
const fmtM = v => v != null ? `$${Math.round(Number(v)).toLocaleString("es-CL")}` : "—";
const quien = e => e ? String(e).replace(/@.*/, "") : "sistema";
const CANAL = { correo: "enviada por correo", descarga: "descargada para enviar", aprobacion: "congelada al aprobar la cotización", confirmacion: "congelada al confirmar" };

const C = {
  ok: "#2ecc71", falta: "#f39c12", txt: "#ddd", sub: "#8a8a8a", borde: "#2e2e2e", fondo: "#171717",
};

const Boton = ({ onClick, children, disabled, tono = "ghost", title }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    style={{
      flex: "1 1 150px", minHeight: 40, padding: "8px 12px", borderRadius: 6, fontSize: 12, cursor: disabled ? "default" : "pointer",
      opacity: disabled ? .6 : 1, fontWeight: 600,
      background: tono === "primario" ? "#14532d" : "transparent",
      color: tono === "primario" ? "#d1fae5" : "#cbd5e1",
      border: `1px solid ${tono === "primario" ? "#2ecc71" : "#444"}`,
    }}>
    {children}
  </button>
);

// Los archivos del expediente no son públicos (la OC trae precios): se abren por la ruta
// autenticada GET /document/:id/download, como blob, en una pestaña nueva.
// Solo se NAVEGA a un PDF o una imagen de esta lista (según el Content-Type que manda el servidor);
// cualquier otro tipo se descarga con su nombre y nunca se abre en la pestaña.
const TIPOS_VISIBLES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"];
const abrirDocumento = async (a, setOcupado) => {
  setOcupado?.(`doc:${a.documentId}`);
  // La pestaña se abre ANTES del await (dentro del gesto del usuario); después de la descarga se le
  // asigna el archivo. Si el navegador la bloqueó (teléfono), se descarga con su nombre.
  let w = null;
  try { w = window.open("", "_blank"); if (w) w.opener = null; } catch (e) { w = null; }
  try {
    const res = await backend.get(`document/${a.documentId}/download`, { responseType: "blob" });
    const tipo = String(res.headers?.["content-type"] || "").split(";")[0].trim().toLowerCase();
    const visible = TIPOS_VISIBLES.includes(tipo);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: visible ? tipo : "application/octet-stream" }));
    if (visible && w && !w.closed) {
      w.location = url;
    } else {
      try { w?.close(); } catch (_) { /* nada */ }
      const el = document.createElement("a"); el.href = url; el.download = a.originalName || "documento"; document.body.appendChild(el); el.click(); el.remove();
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (e) {
    try { w?.close(); } catch (_) { /* nada */ }
    alert("No se pudo abrir el archivo: " + (e?.response?.data?.error || e.message));
  } finally { setOcupado?.(null); }
};

const Archivo = ({ a, etiqueta, ocupado, setOcupado }) => (
  <button type="button" onClick={() => abrirDocumento(a, setOcupado)} disabled={ocupado === `doc:${a.documentId}`}
    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 44, padding: "6px 10px", border: `1px solid ${C.borde}`, borderRadius: 6,
             color: C.txt, textAlign: "left", fontSize: 12, background: "#1e1e1e", cursor: "pointer", opacity: ocupado === `doc:${a.documentId}` ? .6 : 1 }}>
    <span aria-hidden="true">📄</span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{etiqueta || a.label || a.originalName}</span>
      <span style={{ display: "block", fontSize: 11, color: C.sub }}>{fmtFecha(a.en)} · {quien(a.por)}{a.size ? ` · ${Math.round(a.size / 1024)} KB` : ""}</span>
    </span>
    <span style={{ color: C.sub, fontSize: 11 }}>{ocupado === `doc:${a.documentId}` ? "Abriendo…" : "Ver"}</span>
  </button>
);

const Pieza = ({ n, titulo, estado, detalle, children, acciones }) => (
  <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.borde}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <span style={{ width: 22, height: 22, borderRadius: 11, display: "inline-flex", alignItems: "center", justifyContent: "center",
                     fontSize: 11, fontWeight: 700, background: estado === "ok" ? "#0d1f10" : estado === "falta" ? "#2a1f0a" : "#222",
                     color: estado === "ok" ? C.ok : estado === "falta" ? C.falta : C.sub, border: `1px solid ${estado === "ok" ? "#14532d" : estado === "falta" ? "#7c4a03" : C.borde}` }}>
        {estado === "ok" ? "✓" : n}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: C.txt, flex: 1 }}>{titulo}</span>
      {estado === "falta" && <span style={{ fontSize: 11, color: C.falta, fontWeight: 600 }}>Falta</span>}
      {estado === "pendiente" && <span style={{ fontSize: 11, color: C.sub }}>Todavía no corresponde</span>}
    </div>
    {detalle && <div style={{ fontSize: 11, color: C.sub, marginLeft: 30, marginBottom: 6 }}>{detalle}</div>}
    {children && <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 30 }}>{children}</div>}
    {acciones && <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginLeft: 30, marginTop: 8 }}>{acciones}</div>}
  </div>
);

const descargarBlob = (data, nombre) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export default function ExpedienteCompra({ po, onCambio }) {
  // Las acciones (congelar, subir cotización, adjuntar factura) son de admin/operator: las rutas que
  // usan exigen esos roles. Contabilidad (accountant) ve y abre el expediente, sin botones.
  const auth = useContext(AuthContext);
  const puedeEditar = ["admin", "operator"].some(r => (auth?.user?.roles || []).includes(r));
  const [exp, setExp] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [verLineas, setVerLineas] = useState(false);
  const inputCot = useRef(null);
  const inputFac = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await backend.get(`purchaseOrder/${po._id}/expediente`);
      setExp(data); setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [po._id]);

  useEffect(() => { cargar(); }, [cargar, po.status, po.updatedAt]);

  const esSolicitud = po.status === "cotizando" || po.status === "cotizado";
  const confirmada = ["pedido", "enCamino", "recibido"].includes(po.status);

  const congelarYDescargar = async () => {
    setOcupado("pdf");
    try {
      const res = await backend.get(`purchaseOrder/${po._id}/pdf?modo=descargar`, { responseType: "blob" });
      const m = String(res.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      descargarBlob(res.data, m ? m[1] : `${esSolicitud ? "SolicitudCotizacion" : "OrdenCompra"}-${po.number}.pdf`);
      await cargar(); onCambio?.();
    } catch (e) {
      alert("No se pudo generar el PDF: " + (e?.response?.data?.error || e.message));
    } finally { setOcupado(null); }
  };

  const subirCotizacion = async (file) => {
    if (!file) return;
    setOcupado("cot");
    try {
      const fd = new FormData(); fd.append("file", file);
      await backend.post(`purchaseOrder/${po._id}/expediente/cotizacion`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await cargar(); onCambio?.();
    } catch (e) {
      alert("No se pudo guardar la cotización: " + (e?.response?.data?.error || e.message));
    } finally { setOcupado(null); }
  };

  const subirFactura = async (file) => {
    if (!file) return;
    setOcupado("fac");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("docType", "facturaRecibida"); fd.append("purchaseOrderRefs", po._id);
      fd.append("label", `Factura proveedor${po.invoiceNumber ? " N° " + po.invoiceNumber : ""} — OC N°${po.number}`);
      await backend.post("document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await cargar(); onCambio?.();
    } catch (e) {
      alert("No se pudo adjuntar la factura: " + (e?.response?.data?.error || e.message));
    } finally { setOcupado(null); }
  };

  if (error) return <div style={{ fontSize: 11, color: "#e74c3c", margin: "10px 0" }}>Expediente: {error}</div>;
  if (!exp) return <div style={{ fontSize: 11, color: C.sub, margin: "10px 0" }}>Cargando expediente…</div>;

  const sol = exp.solicitud, oc = exp.oc;
  // Con la cotización aprobada (todavía en "cotizado") las líneas cambiaron porque se aprobó: la
  // solicitud congelada es la que se mandó y no se vuelve a congelar.
  const cotizacionAprobada = (["cotizando", "cotizado"].includes(po.status) && !!exp.cotizacionLeida?.aprobadaEn) || !!sol?.fija; // mismo criterio que el backend (+ marca fija)
  const cotRecibidaSinRevisar = exp.cotizacionRecibida && !exp.cotizacionRecibida.revisadaEn;
  const tieneFactura = (exp.facturas?.length || 0) + (exp.facturasArchivo?.length || 0) > 0;

  return (
    <div style={{ background: C.fondo, border: `1px solid ${C.borde}`, borderRadius: 8, padding: "10px 12px", margin: "12px 0" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: "uppercase", letterSpacing: ".04em" }}>
          Expediente de la compra
        </div>
        {exp.faltan?.length ? <span style={{ fontSize: 11, color: C.falta }}>{exp.faltan.length} pendiente{exp.faltan.length === 1 ? "" : "s"}</span>
                            : <span style={{ fontSize: 11, color: C.ok }}>Completo para su estado</span>}
      </div>

      {/* 1. Solicitud */}
      <Pieza n="1" titulo="Solicitud tal como se envió"
        estado={sol?.documentId ? "ok" : po.status === "cancelado" ? "pendiente" : "falta"}
        detalle={sol?.documentId
          ? `${sol.reconstruida ? "reconstruida al confirmar (líneas ya ajustadas a la cotización aprobada; no es lo que vio el proveedor)" : (CANAL[sol.canal] || "congelada")} el ${fmtFecha(sol.en)} por ${quien(sol.por)} · ${sol.lineas?.length || 0} línea${(sol.lineas?.length || 0) === 1 ? "" : "s"}${sol.lineasCambiaronDespues && !sol.reconstruida ? (cotizacionAprobada ? " · líneas ajustadas al aprobar la cotización" : " · ⚠ lo que ve el proveedor cambió después de congelarla") : ""}`
          : sol?.lineas?.length
            ? `Líneas congeladas (${sol.lineas.length}) sin PDF: el generador falló. Vuelva a descargar para generarlo.`
            : "No hay copia de lo que vio el proveedor: la solicitud no se envió ni se descargó desde la app."}
        acciones={<>
          {puedeEditar && esSolicitud && (!sol?.documentId || (sol.lineasCambiaronDespues && !cotizacionAprobada)) && (
            <Boton tono="primario" onClick={congelarYDescargar} disabled={ocupado === "pdf"}>
              {ocupado === "pdf" ? "Generando…" : "⬇ Congelar y descargar PDF"}
            </Boton>
          )}
          {sol?.lineas?.length > 0 && (
            <Boton onClick={() => setVerLineas(v => !v)}>{verLineas ? "Ocultar líneas congeladas" : `Ver líneas congeladas (${sol.lineas.length})`}</Boton>
          )}
        </>}>
        {sol?.documentId && <Archivo a={sol} etiqueta={sol.originalName} ocupado={ocupado} setOcupado={setOcupado} />}
        {verLineas && sol?.lineas?.map((l, i) => (
          <div key={i} style={{ fontSize: 12, color: C.txt, padding: "4px 0", borderBottom: "1px solid #222" }}>
            <MedidaConMm texto={l.description} /> <span style={{ color: C.sub }}>· {l.quantity} {l.unit}</span>
            {l.notes && <div style={{ fontSize: 11, color: C.sub }}>{l.notes}</div>}
          </div>
        ))}
        {sol?.versionesAnteriores?.length > 0 && sol.versionesAnteriores.map(v => <Archivo key={v.documentId} a={v} ocupado={ocupado} setOcupado={setOcupado} />)}
      </Pieza>

      {/* 2. Cotización del proveedor */}
      <Pieza n="2" titulo="Cotización del proveedor"
        estado={exp.cotizaciones?.length ? "ok" : po.status === "cancelado" ? "pendiente" : "falta"}
        detalle={cotRecibidaSinRevisar
          ? `📎 Cotización recibida en recepcion@ el ${fmtFecha(exp.cotizacionRecibida.en)} (${exp.cotizacionRecibida.remitente || "proveedor"}) — revisar con "Leer y comparar".`
          : exp.cotizacionLeida?.leidaEn
            ? `Leída el ${fmtFecha(exp.cotizacionLeida.leidaEn)} por ${quien(exp.cotizacionLeida.leidaPor)}${exp.cotizacionLeida.numero ? ` · Cot. N°${exp.cotizacionLeida.numero}` : ""}${exp.cotizacionLeida.aprobadaEn ? ` · aprobada el ${fmtFecha(exp.cotizacionLeida.aprobadaEn)}` : ""}`
            : exp.cotizaciones?.length ? "Archivo guardado; todavía no se leyó con el lector." : "Falta el archivo de la cotización del proveedor (PDF o foto)."}
        acciones={puedeEditar && po.status !== "cancelado" && (
          <>
            <Boton onClick={() => inputCot.current?.click()} disabled={ocupado === "cot"}>{ocupado === "cot" ? "Guardando…" : "📎 Subir cotización al expediente"}</Boton>
            <input ref={inputCot} type="file" accept="application/pdf,image/*" style={{ display: "none" }}
              onChange={e => { subirCotizacion(e.target.files?.[0]); e.target.value = ""; }} />
          </>
        )}>
        {exp.cotizaciones?.map(c => <Archivo key={c.documentId} a={c} etiqueta={`${c.label || c.originalName}${c.monto ? ` · ${fmtM(c.monto)} neto` : ""}`} ocupado={ocupado} setOcupado={setOcupado} />)}
      </Pieza>

      {/* 3. OC confirmada */}
      <Pieza n="3" titulo="Orden de compra tal como se confirmó"
        estado={oc?.documentId ? "ok" : confirmada ? "falta" : "pendiente"}
        detalle={oc?.documentId
          ? `${oc.reconstruida ? "PDF reconstruido" : "Confirmada"} el ${fmtFecha(oc.en)} por ${quien(oc.por)}${oc.reconstruida ? " (desde el estado actual: no es el que vio el proveedor)" : ""} · ${oc.lineas?.length || 0} línea${(oc.lineas?.length || 0) === 1 ? "" : "s"} · ${fmtM(oc.total)} neto${oc.cambioDespues ? " · ⚠ la OC cambió después de confirmarse; el PDF guardado es el confirmado" : ""}`
          : confirmada ? "La OC está confirmada pero su PDF no quedó guardado." : "Se genera al confirmar la compra."}
        acciones={puedeEditar && confirmada && !oc?.documentId && (
          <Boton tono="primario" onClick={congelarYDescargar} disabled={ocupado === "pdf"}>{ocupado === "pdf" ? "Generando…" : "⬇ Generar y guardar PDF de la OC"}</Boton>
        )}>
        {oc?.documentId && <Archivo a={oc} etiqueta={oc.originalName} ocupado={ocupado} setOcupado={setOcupado} />}
        {oc?.versionesAnteriores?.length > 0 && oc.versionesAnteriores.map(v => <Archivo key={v.documentId} a={v} ocupado={ocupado} setOcupado={setOcupado} />)}
      </Pieza>

      {/* 4. Factura */}
      <Pieza n="4" titulo="Factura del proveedor"
        estado={tieneFactura ? "ok" : po.status === "recibido" ? "falta" : "pendiente"}
        detalle={tieneFactura
          ? (exp.facturas?.length ? `Vinculada por el ${exp.facturas[0].origen === "sii_api" || exp.facturas[0].origen === "sii_csv" ? "sync del SII" : "registro de compras"}.` : "Archivo adjuntado al recibir.")
          : exp.anotadaAlRecibir
            ? `Al recibir se anotó la factura N°${exp.anotadaAlRecibir.invoiceNumber}${exp.anotadaAlRecibir.invoiceAmount ? ` por ${fmtM(exp.anotadaAlRecibir.invoiceAmount)}` : ""}; falta el archivo o el cruce con el SII.`
            : po.status === "recibido" ? "Material recibido sin factura vinculada." : "Llega con el material o por el sync del SII."}
        acciones={puedeEditar && confirmada && (
          <>
            <Boton onClick={() => inputFac.current?.click()} disabled={ocupado === "fac"}>{ocupado === "fac" ? "Guardando…" : "📎 Adjuntar factura"}</Boton>
            <input ref={inputFac} type="file" accept="application/pdf,image/*" style={{ display: "none" }}
              onChange={e => { subirFactura(e.target.files?.[0]); e.target.value = ""; }} />
          </>
        )}>
        {exp.facturas?.map(f => (
          <div key={f.facturaId} style={{ fontSize: 12, color: C.txt, display: "flex", gap: 8, flexWrap: "wrap", minHeight: 24, alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>Factura {f.folio}</span>
            <span style={{ color: C.sub }}>{f.razonEmisor || f.rutEmisor}</span>
            <span style={{ color: C.sub }}>{f.fecha ? new Date(f.fecha).toLocaleDateString("es-CL") : ""}</span>
            <span style={{ color: C.ok, fontWeight: 600 }}>{fmtM(f.montoTotal)}</span>
          </div>
        ))}
        {exp.facturasArchivo?.map(a => <Archivo key={a.documentId} a={a} ocupado={ocupado} setOcupado={setOcupado} />)}
        {exp.certificados?.map(a => <Archivo key={a.documentId} a={a} etiqueta={`Certificado: ${a.label || a.originalName}`} ocupado={ocupado} setOcupado={setOcupado} />)}
      </Pieza>

      {(exp.faltan?.length > 0 || exp.avisos?.length > 0) && (
        <div style={{ marginTop: 8, fontSize: 11, color: C.falta, lineHeight: 1.4 }}>
          {exp.faltan?.map((f, i) => <div key={`f${i}`}>⚠ {f.texto}</div>)}
          {exp.avisos?.map((a, i) => <div key={`a${i}`} style={{ color: C.sub }}>ⓘ {a.texto}</div>)}
        </div>
      )}
    </div>
  );
}
